<template>
  <v-app>
    <div v-if="!loaded"><loader/></div>
    <v-container fluid v-else class="pa-2 pa-sm-3">

      <!-- Search Filter -->
      <v-card class="mb-3 pa-3">
        <v-layout row wrap align-center>
          <v-flex xs12 sm6 md3 class="pr-1 pb-1">
            <date-picker v-model="start" :get-classes="getRangeClasses" :default-value="end || new Date()"
              :disabled-date="disabledStartDate" type="date" placeholder="Start Date" style="width:100%;"/>
          </v-flex>
          <v-flex xs12 sm6 md3 class="pr-1 pb-1">
            <date-picker v-model="end" :get-classes="getRangeClasses" :default-value="start || new Date()"
              :disabled-date="disabledEndDate" type="date" placeholder="End Date" style="width:100%;"/>
          </v-flex>
          <v-flex xs12 sm6 md2 class="pr-1 pb-1">
            <v-select v-model="option" :items="options" item-text="text" item-value="value"
              label="Option" hide-details @change="onChange"/>
          </v-flex>
          <v-flex xs12 sm6 md2 class="pr-1 pb-1" v-if="option === 'Payment'">
            <v-select v-model="payments" :items="paymentOptions" label="Payment" hide-details/>
          </v-flex>
          <v-flex xs12 sm6 md2 class="pr-1 pb-1" v-if="option === 'Product'">
            <v-select v-if="allProduct" v-model="product_id" :items="transformProduct(allProduct)"
              item-text="text" item-value="value" label="Product" hide-details/>
          </v-flex>
          <v-flex xs12 sm6 md2 class="pr-1 pb-1" v-if="option === 'Product'">
            <v-select v-model="freq" :items="items" label="Frequency" hide-details/>
          </v-flex>
          <v-flex xs12 sm6 md2 class="pr-1 pb-1" v-if="option === 'Bank'">
            <v-select v-model="accounts" :items="accountOptions" label="Bank Account" hide-details/>
          </v-flex>
          <v-flex xs12 sm6 md2 class="pb-1">
            <v-btn color="primary" block @click="validate">Search</v-btn>
          </v-flex>
        </v-layout>
        <div v-if="result" class="mt-2 title font-weight-bold">Result: {{ result }}</div>
      </v-card>

      <!-- Stat Cards -->
      <v-layout row wrap>
        <v-flex xs6 sm4 md2 class="pa-1" v-for="(card, i) in statCards" :key="i">
          <home-card :title="card.title" :number="card.value"/>
        </v-flex>
      </v-layout>

      <!-- Charts Row -->
      <v-layout row wrap class="mt-3">
        <v-flex xs12 md6 class="pa-1">
          <v-card class="pa-2" style="height:320px;">
            <v-card-title class="subheading font-weight-bold pb-1">Earnings by Product</v-card-title>
            <div style="position:relative; height:250px;">
              <canvas id="earningsChart"></canvas>
            </div>
          </v-card>
        </v-flex>
        <v-flex xs12 md6 class="pa-1">
          <v-card class="pa-2" style="height:320px;">
            <v-card-title class="subheading font-weight-bold pb-1">Funded vs Unfunded Users</v-card-title>
            <div style="position:relative; height:250px;">
              <canvas id="usersChart"></canvas>
            </div>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Product Earnings Table -->
      <v-card class="mt-3">
        <v-card-title class="subheading font-weight-bold">Product Earnings</v-card-title>
        <v-card-text class="pa-0">
          <div style="overflow-x:auto;">
            <table style="width:100%; border-collapse:collapse; font-size:13px;">
              <thead>
                <tr style="background:#1565c0; color:#fff;">
                  <th style="padding:10px; text-align:left;">Product</th>
                  <th style="padding:10px; text-align:right;">Total Investment</th>
                  <th style="padding:10px; text-align:right;">Total Interest</th>
                  <th style="padding:10px; text-align:right;">Interest Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr style="background:#e8f5e9;">
                  <td colspan="4" style="padding:8px 10px; font-weight:bold;">Active Products</td>
                </tr>
                <template v-if="activeProductBalances.length">
                  <tr v-for="(item, i) in activeProductBalances" :key="'a'+i"
                    :style="i%2===0?'background:#f9f9f9':''">
                    <td style="padding:8px 10px;">{{ item.name }}</td>
                    <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(item.amount) }}</td>
                    <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(item.interest) }}</td>
                    <td style="padding:8px 10px; text-align:right;">{{ item.interest_rate | percent(2) }}</td>
                  </tr>
                  <tr style="background:#c8e6c9; font-weight:bold;">
                    <td style="padding:8px 10px;">TOTAL</td>
                    <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(sumField(activeProductBalances,'amount')) }}</td>
                    <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(sumField(activeProductBalances,'interest')) }}</td>
                    <td></td>
                  </tr>
                </template>
                <tr v-else><td colspan="4" style="padding:10px; text-align:center; color:#999;">No active data</td></tr>

                <tr style="height:8px; background:#e0e0e0;"><td colspan="4"></td></tr>

                <tr style="background:#fce4ec;">
                  <td colspan="4" style="padding:8px 10px; font-weight:bold;">Inactive Products</td>
                </tr>
                <template v-if="inactiveProductBalances.length">
                  <tr v-for="(item, i) in inactiveProductBalances" :key="'n'+i"
                    :style="i%2===0?'background:#f9f9f9':''">
                    <td style="padding:8px 10px;">{{ item.name }}</td>
                    <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(item.amount) }}</td>
                    <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(item.interest) }}</td>
                    <td style="padding:8px 10px; text-align:right;">{{ item.interest_rate | percent(2) }}</td>
                  </tr>
                  <tr style="background:#f8bbd0; font-weight:bold;">
                    <td style="padding:8px 10px;">TOTAL</td>
                    <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(sumField(inactiveProductBalances,'amount')) }}</td>
                    <td style="padding:8px 10px; text-align:right;">{{ formatCurrency(sumField(inactiveProductBalances,'interest')) }}</td>
                    <td></td>
                  </tr>
                </template>
                <tr v-else><td colspan="4" style="padding:10px; text-align:center; color:#999;">No inactive data</td></tr>
              </tbody>
            </table>
          </div>
        </v-card-text>
      </v-card>

    </v-container>

    <v-snackbar v-model="toast.show" top right color="black">
      {{ toast.msg }}
      <v-btn flat dark small @click.native="toast.show = false">Close</v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import loader from '@/components/loader.vue';
import homeCard from '@/components/homeCard.vue';
import productMixin from '../../mixins/products.mixin';
import Vue from 'vue';

export default {
  name: 'Home',
  components: { loader, homeCard },
  mixins: [productMixin],
  data() {
    return {
      valid: true,
      start: null, end: null, product_id: null, freq: null,
      option: null, payments: null, accounts: null,
      result: null,
      toast: { show: false, msg: '' },
      items: [null, 'Daily', 'Weekly', 'Monthly'],
      options: [null, 'Payment', 'Product', 'Bank'],
      paymentOptions: ['Yes', 'No'],
      accountOptions: ['Yes', 'No'],
      earningsChartInstance: null,
      usersChartInstance: null,
    };
  },
  computed: {
    ...mapGetters(['loaded', 'account', 'allProduct', 'allProductBalances']),
    activeProductBalances() {
      return (this.allProductBalances || []).filter(i => i.status === 'active');
    },
    inactiveProductBalances() {
      return (this.allProductBalances || []).filter(i => i.status === 'inactive');
    },
    statCards() {
      const a = this.account || {};
      return [
        { title: 'Total Earnings', value: this.$options.filters.commas(this.$options.filters.currency(a.paystackBalance || 0, '₦', 2)) },
        { title: 'Total Users', value: this.$options.filters.commas(a.userCount || 0) },
        { title: 'Transactions', value: this.$options.filters.commas(a.transactionCount || 0) },
        { title: 'Daily Users', value: a.userDailyCount || 0 },
        { title: 'Weekly Users', value: a.userWeeklyCount || 0 },
        { title: 'Monthly Users', value: a.userMonthlyCount || 0 },
        { title: 'Funded Users', value: this.$options.filters.commas(a.userFundedCount || 0) },
        { title: 'Unfunded Users', value: this.$options.filters.commas(a.userUnFundedCount || 0) },
        { title: 'Account Balance', value: this.$options.filters.commas(this.$options.filters.currency(a.allAccountBalance || 0, '₦', 2)) },
        { title: 'Locked Balance', value: this.$options.filters.commas(this.$options.filters.currency(a.allLockedBalance || 0, '₦', 2)) },
        { title: 'Total Withdrawn', value: this.$options.filters.commas(this.$options.filters.currency(a.totalWithdrawals || 0, '₦', 2)) },
      ];
    },
  },
  watch: {
    allProductBalances(val) {
      if (val) this.$nextTick(() => this.renderCharts());
    },
    account(val) {
      if (val) this.$nextTick(() => this.renderUserChart());
    },
  },
  created() {
    this.getProfile();
    this.getAllProducts();
    this.getAllProductBalances();
  },
  mounted() {
    this.loadChartJs();
  },
  methods: {
    ...mapActions(['getProfile', 'getAllProducts', 'getAllProductBalances']),
    sumField(arr, field) {
      return arr.reduce((s, i) => s + parseFloat(i[field] || 0), 0);
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount || 0);
    },
    getRangeClasses(cellDate, currentDates, classnames) {
      const classes = [];
      const start = this.start && new Date(this.start).setHours(0,0,0,0);
      const end = this.end && new Date(this.end).setHours(0,0,0,0);
      if (!/disabled|active|not-current-month/.test(classnames) && start && end &&
        cellDate.getTime() >= start && cellDate.getTime() <= end) classes.push('in-range');
      return classes;
    },
    disabledStartDate(date) {
      return this.end && new Date(date).setHours(0,0,0,0) > new Date(this.end).setHours(0,0,0,0);
    },
    disabledEndDate(date) {
      return this.start && new Date(date).setHours(0,0,0,0) < new Date(this.start).setHours(0,0,0,0);
    },
    onChange() {},
    validate() {
      if (!this.start || !this.end) { this.toast.msg = 'Select start and end date'; this.toast.show = true; return; }
      this.valid = false;
      Vue.axios.post('admin/user-by-date', {
        start: this.start, end: this.end, product_id: this.product_id,
        freq: this.freq, payment: this.payments, account: this.accounts, option: this.option,
      }).then((res) => {
        this.result = res.data.data.count;
        this.toast.msg = 'Result fetched successfully';
        this.toast.show = true;
        this.valid = true;
      }).catch(() => { this.valid = true; });
    },
    loadChartJs() {
      if (window.Chart) { this.renderCharts(); return; }
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/chart.js@3.9.1/dist/chart.min.js';
      s.onload = () => this.renderCharts();
      document.head.appendChild(s);
    },
    renderCharts() {
      this.renderEarningsChart();
      this.renderUserChart();
    },
    renderEarningsChart() {
      if (!window.Chart || !this.allProductBalances) return;
      const active = this.activeProductBalances;
      if (!active.length) return;
      const ctx = document.getElementById('earningsChart');
      if (!ctx) return;
      if (this.earningsChartInstance) this.earningsChartInstance.destroy();
      this.earningsChartInstance = new window.Chart(ctx, {
        type: 'bar',
        data: {
          labels: active.map(i => i.name),
          datasets: [
            { label: 'Investment', data: active.map(i => parseFloat(i.amount || 0)), backgroundColor: '#1565c0' },
            { label: 'Interest', data: active.map(i => parseFloat(i.interest || 0)), backgroundColor: '#43a047' },
          ],
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } } },
      });
    },
    renderUserChart() {
      if (!window.Chart || !this.account) return;
      const ctx = document.getElementById('usersChart');
      if (!ctx) return;
      if (this.usersChartInstance) this.usersChartInstance.destroy();
      this.usersChartInstance = new window.Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Funded', 'Unfunded'],
          datasets: [{
            data: [this.account.userFundedCount || 0, this.account.userUnFundedCount || 0],
            backgroundColor: ['#43a047', '#e53935'],
          }],
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } },
      });
    },
  },
};
</script>

<style scoped>
input.mx-input { border: 1px solid #ccc; }
</style>
